// ReservationModal.jsx
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Overlay,
  Modal,
  Frame,
  Header,
  Ribbon,
  Subtitle,
  Title,
  Dots,
  CloseBtn,
  Form,
  Row,
  Field,
  Label,
  Input,
  Select,
  Textarea,
  Actions,
  SubmitBtn,
  CancelBtn,
  ErrorMsg,
  HeaderOrnament,
  OrnDivider,
  SuccessNote,
} from "./ReservationModalStyles";

/**
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - onSubmit: async (payload) => void
 */
export default function ReservationModal({ isOpen, onClose, onSubmit }) {
  const { t } = useTranslation(); // folosește default namespace + "reservation" în chei

  const [nume, setNume] = useState("");
  const [email, setEmail] = useState("");
  const [telefon, setTelefon] = useState("");
  const [persoane, setPersoane] = useState("");
  const [data, setData] = useState("");
  const [ora, setOra] = useState("");
  const [mentiuni, setMentiuni] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // sloturi 08:00–21:30 din 30 în 30 min
  const timeOptions = useMemo(() => {
    const out = [];
    for (let h = 8; h <= 21; h++) {
      out.push(`${String(h).padStart(2, "0")}:00`);
      out.push(`${String(h).padStart(2, "0")}:30`);
    }
    return out;
  }, []);

  const validate = () => {
    const e = {};
    if (!nume.trim()) e.nume = t("reservation.errors.nameRequired");
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      e.email = t("reservation.errors.emailInvalid");
    if (!telefon.trim()) e.telefon = t("reservation.errors.phoneRequired");
    if (!persoane.toString().trim())
      e.persoane = t("reservation.errors.peopleRequired");
    if (persoane && !/^\d+$/.test(String(persoane)))
      e.persoane = t("reservation.errors.peopleInteger");
    if (!data) e.data = t("reservation.errors.dateRequired");
    if (!ora) e.ora = t("reservation.errors.timeRequired");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const resetForm = () => {
    setNume("");
    setEmail("");
    setTelefon("");
    setPersoane("");
    setData("");
    setOra("");
    setMentiuni("");
    setErrors({});
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    const payload = {
      nume: nume.trim(),
      email: email.trim() || null,
      telefon: telefon.trim(),
      persoane: Number(persoane),
      data,
      ora,
      mentiuni: mentiuni.trim() || null,
    };
    try {
      setSubmitting(true);
      await onSubmit?.(payload);
      resetForm();
      setSent(true); // ✅ arată mesajul de succes
      setTimeout(() => {
        // ✅ închide după scurtă pauză
        setSent(false);
        onClose?.();
      }, 1400);
    } catch (err) {
      console.error("Eroare la salvare rezervare:", err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onMouseDown={onClose}>
      <Modal
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t("reservation.a11y.modalLabel")}
      >
        <Frame>
          <Header>
            <Ribbon />
            <Subtitle>{t("reservation.header.subtitle")}</Subtitle>
            <Title>{t("reservation.header.title")}</Title>
            <HeaderOrnament aria-hidden />
            <Dots aria-hidden>·····</Dots>
            <CloseBtn
              onClick={onClose}
              aria-label={t("reservation.actions.close")}
            />
          </Header>

          <Form onSubmit={handleSubmit}>
            <Row>
              <Field>
                <Label htmlFor="nume">{t("reservation.fields.name")}*</Label>
                <Input
                  id="nume"
                  value={nume}
                  onChange={(e) => setNume(e.target.value)}
                  placeholder={t("reservation.placeholders.fullName")}
                  autoFocus
                />
                {errors.nume && <ErrorMsg>{errors.nume}</ErrorMsg>}
              </Field>

              <Field>
                <Label htmlFor="email">{t("reservation.fields.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("reservation.placeholders.email")}
                />
                {errors.email && <ErrorMsg>{errors.email}</ErrorMsg>}
              </Field>
            </Row>

            <Row>
              <Field>
                <Label htmlFor="telefon">
                  {t("reservation.fields.phone")}*
                </Label>
                <Input
                  id="telefon"
                  value={telefon}
                  onChange={(e) => setTelefon(e.target.value)}
                  placeholder={t("reservation.placeholders.phone")}
                />
                {errors.telefon && <ErrorMsg>{errors.telefon}</ErrorMsg>}
              </Field>

              <Field>
                <Label htmlFor="persoane">
                  {t("reservation.fields.people")}*
                </Label>
                <Input
                  id="persoane"
                  inputMode="numeric"
                  pattern="\d*"
                  value={persoane}
                  onChange={(e) => setPersoane(e.target.value)}
                  placeholder={t("reservation.placeholders.people")}
                />
                {errors.persoane && <ErrorMsg>{errors.persoane}</ErrorMsg>}
              </Field>
            </Row>

            <Row>
              <Field>
                <Label htmlFor="data">{t("reservation.fields.date")}*</Label>
                <Input
                  id="data"
                  type="date"
                  value={data}
                  onChange={(e) => setData(e.target.value)}
                />
                {errors.data && <ErrorMsg>{errors.data}</ErrorMsg>}
              </Field>

              <Field>
                <Label htmlFor="ora">{t("reservation.fields.time")}*</Label>
                <Select
                  id="ora"
                  value={ora}
                  onChange={(e) => setOra(e.target.value)}
                >
                  <option value="" disabled>
                    {t("reservation.placeholders.select")}
                  </option>
                  {timeOptions.map((tOpt) => (
                    <option key={tOpt} value={tOpt}>
                      {tOpt}
                    </option>
                  ))}
                </Select>
                {errors.ora && <ErrorMsg>{errors.ora}</ErrorMsg>}
              </Field>
            </Row>

            <Field>
              <Label htmlFor="mentiuni">{t("reservation.fields.notes")}</Label>
              <Textarea
                id="mentiuni"
                rows={4}
                value={mentiuni}
                onChange={(e) => setMentiuni(e.target.value)}
                placeholder={t("reservation.placeholders.notes")}
              />
            </Field>

            <HeaderOrnament aria-hidden />
            <Actions>
              <CancelBtn type="button" onClick={onClose}>
                {t("reservation.actions.cancel")}
              </CancelBtn>
              <SubmitBtn type="submit" disabled={submitting} aria-live="polite">
                <span>
                  {submitting
                    ? t("reservation.actions.sending")
                    : t("reservation.actions.submit")}
                </span>
                {!submitting && (
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 12h12M13 6l6 6-6 6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </SubmitBtn>
            </Actions>
          </Form>
          {sent && (
            <SuccessNote role="status" aria-live="polite">
              {t("reservation.success")}
            </SuccessNote>
          )}
        </Frame>
      </Modal>
    </Overlay>
  );
}
